import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { BRAND } from '@/lib/brand';

const API = `${window.location.origin}/api`;

const SettingsContext = createContext(null);

// Convert hex to HSL values for Tailwind CSS variables
function hexToHSL(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Determine if a color is light or dark (for foreground contrast)
function isLightColor(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6;
}

function applyTheme(settings) {
  if (!settings) return;
  const root = document.documentElement;

  // Primary color -> update Tailwind's --primary
  const primary = settings.color_primary || BRAND.colors.primary;
  if (primary && primary.startsWith('#')) {
    const hsl = hexToHSL(primary);
    root.style.setProperty('--primary', hsl);
    root.style.setProperty('--ring', hsl);
    root.style.setProperty('--sidebar-primary', hsl);
    root.style.setProperty('--chart-1', hsl);
    // Set foreground based on lightness
    if (isLightColor(primary)) {
      root.style.setProperty('--primary-foreground', '15 12% 15%');
      root.style.setProperty('--sidebar-primary-foreground', '15 12% 15%');
    } else {
      root.style.setProperty('--primary-foreground', '0 0% 100%');
      root.style.setProperty('--sidebar-primary-foreground', '0 0% 100%');
    }
  }

  // Accent color
  const accent = settings.color_accent || BRAND.colors.accent;
  if (accent && accent.startsWith('#')) {
    const hsl = hexToHSL(accent);
    root.style.setProperty('--accent', hsl);
    root.style.setProperty('--chart-2', hsl);
  }

  // Background color
  const bg = settings.color_background || BRAND.colors.background;
  if (bg && bg.startsWith('#')) {
    root.style.setProperty('--background', hexToHSL(bg));
    root.style.setProperty('--sidebar-background', hexToHSL(bg));
  }

  // Text color -> foreground
  const text = settings.color_text || BRAND.colors.text;
  if (text && text.startsWith('#')) {
    const hsl = hexToHSL(text);
    root.style.setProperty('--foreground', hsl);
    root.style.setProperty('--card-foreground', hsl);
    root.style.setProperty('--popover-foreground', hsl);
    root.style.setProperty('--sidebar-foreground', hsl);
  }

  // Secondary / muted derived from accent
  if (accent && accent.startsWith('#')) {
    // Make secondary a lighter version
    const hex = accent.replace('#', '');
    const r = Math.min(255, parseInt(hex.substring(0, 2), 16) + 40);
    const g = Math.min(255, parseInt(hex.substring(2, 4), 16) + 40);
    const b = Math.min(255, parseInt(hex.substring(4, 6), 16) + 40);
    const lighter = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    root.style.setProperty('--secondary', hexToHSL(lighter));
    root.style.setProperty('--muted', hexToHSL(lighter));
    root.style.setProperty('--sidebar-accent', hexToHSL(lighter));
  }

  // Border derived from accent (lighter)
  if (accent && accent.startsWith('#')) {
    const hex = accent.replace('#', '');
    const r = Math.min(255, parseInt(hex.substring(0, 2), 16) + 30);
    const g = Math.min(255, parseInt(hex.substring(2, 4), 16) + 30);
    const b = Math.min(255, parseInt(hex.substring(4, 6), 16) + 30);
    const borderColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    root.style.setProperty('--border', hexToHSL(borderColor));
    root.style.setProperty('--input', hexToHSL(borderColor));
    root.style.setProperty('--sidebar-border', hexToHSL(borderColor));
  }
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(res => { setSettings(res.data); applyTheme(res.data); })
      .catch(() => { setSettings({}); })
      .finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
      applyTheme(res.data);
    } catch (e) {  }
  };

  return (
    <SettingsContext.Provider value={{ settings: settings || {}, loading, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}

export function getBrand(settings) {
  return {
    name: settings?.salon_name || BRAND.name,
    tagline: settings?.tagline || BRAND.tagline,
    logo: settings?.logo_url || BRAND.logo,
    iconLogo: settings?.icon_logo_url || settings?.logo_url || BRAND.logo,
    colorPrimary: settings?.color_primary || BRAND.colors.primary,
    colorAccent: settings?.color_accent || BRAND.colors.accent,
    colorBackground: settings?.color_background || BRAND.colors.background,
    colorText: settings?.color_text || BRAND.colors.text,
    socialInstagram: settings?.social_instagram || BRAND.social.instagram,
    socialTiktok: settings?.social_tiktok || BRAND.social.tiktok,
    socialFacebook: settings?.social_facebook || BRAND.social.facebook,
    socialWhatsapp: settings?.social_whatsapp || BRAND.social.whatsapp,
    socialLinkedin: settings?.social_linkedin || '',
    socialWebsite: settings?.social_website || BRAND.website,
    phone: settings?.phone || BRAND.phone,
    email: settings?.email || BRAND.email,
    address: settings?.address || BRAND.location,
  };
}
